import React, { useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { ExpandCircleDownOutlined } from '@mui/icons-material'

import GA4 from 'helpers/ga4'

import { color } from 'styles/colors'
import Tooltip from 'components/Tooltip'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ANIMATION_TIME = (window as any).runningSpecs ? 0 : 400

type AwairInfoPanelProps = { className?: string }

const AwairInfoPanel = ({ className = '' }: AwairInfoPanelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const panelContentRef = useRef(null)

  const onClick = useCallback(() => {
    if (panelContentRef.current && isOpen) {
      $(panelContentRef.current).slideUp(ANIMATION_TIME)
    } else if (panelContentRef.current && !isOpen) {
      $(panelContentRef.current).slideDown(ANIMATION_TIME)
    }

    const eventData = { category: 'Customer Detail Page', action: 'Change Awair Info Display', label: isOpen ? 'Close' : 'Open' }

    GA4.sendEvent(eventData)

    setIsOpen(!isOpen)
  }, [isOpen])

  return (
    <div {...{ className }}>
      <S.Header {...{ onClick }}>
        <Tooltip title={isOpen ? 'Click to minimize' : 'Click to expand'}>
          <S.CaretIcon isOpen={isOpen} />
        </Tooltip>
        <b>What is Awair?</b>
      </S.Header>
      <S.Content {...{ isOpen }} ref={panelContentRef}>
        <b>What is Awair?</b>

        <S.IndentedSection>Awair is a home indoor air quality monitor. The all-in-one environmental sensor monitors temperature, humidity, CO2, certain chemicals, and PM2.5.</S.IndentedSection>

        <b>Awair monitors five indoor air factors that impact indoor air quality and home health.</b>

        <S.IndentedSection>
          <ul>
            <S.ListItem><b>Temperature:</b> can influence occupant comfort and well-being. The goal is to strike a balance between energy usage and wellness targets</S.ListItem>
            <S.ListItem><b>Humidity:</b> too much or too little moisture can affect occupant  comfort and cause damage to belongings. Maintaining proper humidity levels can suppress bacteria and mold growth, protect wood floors and furniture, and support a heathy immune response</S.ListItem>
            <S.ListItem><b>CO2:</b> measured in ppm (parts per million), overexposure to carbon dioxide can impair focus, decision-making, and productivity.</S.ListItem>
            <S.ListItem><b>Chemicals(TVOCs):</b> measured in ppb (parts per billion), toxic chemicals can be found in building materials, some home and personal care products, and can be produced through some every day activities such as cooking, burning wood, and smoking. The effects of high-level exposure can cause respiratory irritation, headaches, and worsening of asthma symptoms.</S.ListItem>
            <S.ListItem><b>PM2.5:</b> measured in µg/m³ (micrograms per cubic meter of air), particulate matter can penetrate deep into lung passageways and enter the bloodstream, triggering cardiovascular problems and respiratory impacts like asthma and allergies.</S.ListItem>
          </ul>

          Visit Awair’s website at <a href="http://www.getawair.com/home" target="_blank" rel="noopener noreferrer">http://www.getawair.com/home</a>  to learn more.
        </S.IndentedSection>

        <b>What can I do to help?</b>

        <S.IndentedSection>
          Use the runtime history and Awair data to help pinpoint possible indoor air quality issues.<br />
          View the homeowner’s 30 day history of Awair scores alongside the HVAC values to gain a clearer picture of what may be happening.<br />
          If homeowners have additional concerns such as allergies, asthma, dust, etc., they may be an ideal candidate for an additional IAQ upgrade, such as CleanEffects® or AccuClean®
        </S.IndentedSection>

        <b>How to purchase</b>

        <S.IndentedSection>Visit Awair’s website at <a href="http://www.getawair.com/home" target="_blank" rel="noopener noreferrer">http://www.getawair.com/home</a> and click “Buy Now”.</S.IndentedSection>
      </S.Content>
    </div>
  )
}

export default AwairInfoPanel

const S = {
  Header: styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
    padding: 10px;
    text-align: initial;
    font-weight: bold;
    color: ${color('infoPanelHeaderText')};
    background-color: ${color('infoPanelHeaderBackground')};
    border-radius: 0;
  `,
  CaretIcon: styled(ExpandCircleDownOutlined).withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop)
  })<{ isOpen: boolean }>`
    font-size: 18px;
    margin-right: 10px;
    transform: ${({ isOpen }) => !isOpen && 'rotate(-90deg)'};
  `,
  Content: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop)
  })<{ isOpen: boolean }>`
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};

    padding: 15px 30px 15px;
    background-color: ${color('infoPanelContentBackground')};
  `,
  IndentedSection: styled.div`
    padding: 10px;
  `,
  ListItem: styled.li`
    padding: 3px 0 3px;
  `
}
